import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";

const GAP = 16;
const MORE_W = 72;

export default function NavOverflow({ items, renderItem, renderMenuItem, className = "" }) {
  const wrap = useRef(null);
  const measure = useRef(null);
  const [visible, setVisible] = useState(items.length);
  const [open, setOpen] = useState(false);

  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const compute = () => {
      const widths = Array.from(measure.current?.children || []).map((c) => c.offsetWidth);
      const total = widths.reduce((a, w) => a + w, 0) + GAP * Math.max(0, widths.length - 1);
      const avail = el.clientWidth;
      if (total <= avail) return setVisible(widths.length);
      let used = MORE_W, n = 0;
      for (const w of widths) { if (used + w + GAP > avail) break; used += w + GAP; n += 1; }
      setVisible(n);
    };
    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(el);
    return () => ro.disconnect();
  }, [items]);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const shown = items.slice(0, visible);
  const rest = items.slice(visible);
  return (
    <div ref={wrap} className={`relative flex-1 min-w-0 hidden xl:flex items-center justify-center ${className}`} data-testid="nav-links">
      <div ref={measure} aria-hidden className="absolute invisible pointer-events-none flex items-center whitespace-nowrap" style={{ gap: GAP, left: 0, top: -9999 }}>
        {items.map((n) => <span key={n.id}>{renderItem(n)}</span>)}
      </div>
      <div className="flex items-center whitespace-nowrap" style={{ gap: GAP }}>
        {shown.map((n) => <React.Fragment key={n.id}>{renderItem(n)}</React.Fragment>)}
        {rest.length > 0 && (
          <div className="relative">
            <button type="button" onClick={() => setOpen(!open)} data-testid="nav-more-btn" aria-expanded={open} className="inline-flex items-center gap-1 py-1 hover:text-[#0D3B2E] transition-colors">More <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /></button>
            {open && (
              <div className="absolute right-0 top-full pt-2 z-50" data-testid="nav-more-menu">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-2 min-w-[200px]" onClick={() => setOpen(false)}>
                  {rest.map((n) => <div key={n.id}>{renderMenuItem(n)}</div>)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
